import React, { useMemo, useState, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  Eye, FileSpreadsheet, Layers, AlertCircle, TrendingUp, Hash,
  ChevronDown, ChevronRight, Maximize2, Minimize2,
  ArrowUp, ArrowDown, Lock, Unlock, GripVertical, X,
  Plus, ArrowUpDown,
} from 'lucide-react';

import type {
  ExportJobRow, ExportTemplateColumn, AddressAliasRule,
  MarginRule, MarginResult, InvoiceAssignment, InvoiceRunSummary,
  TownClassification, SortSpec,
} from './types';
import { resolveColumnValueWithMargins, augmentRowWithInvoiceData } from './transforms';

// ─── Props ──────────────────────────────────────────────────────────

interface OutputPreviewProps {
  columns: ExportTemplateColumn[];
  rows: ExportJobRow[];
  aliasRules: AddressAliasRule[];
  marginRules?: MarginRule[];
  templateName?: string;
  invoiceAssignments?: Map<string, InvoiceAssignment> | null;
  invoiceSummary?: InvoiceRunSummary | null;
  townClassifications?: Map<string, TownClassification>;
  /** Export's Date To filter (YYYY-MM-DD), used by date columns in "Last Day of Export" mode */
  rangeEndDate?: string;
  /** Controlled sort specs from parent */
  sortSpecs: SortSpec[];
  /** Callback when sort changes (header click, remove, clear, direction toggle) */
  onSortSpecsChange: (specs: SortSpec[]) => void;
}


// ─── Component ──────────────────────────────────────────────────────

const OutputPreview: React.FC<OutputPreviewProps> = ({
  columns, rows, aliasRules, marginRules = [], templateName,
  invoiceAssignments = null, invoiceSummary = null, townClassifications,
  rangeEndDate,
  sortSpecs, onSortSpecsChange,
}) => {

  const [collapsed, setCollapsed] = useState(false);
  const [poppedOut, setPoppedOut] = useState(false);

  // ─── Column Order & Lock State ──────────────────────────────────
  const [columnOrderOverride, setColumnOrderOverride] = useState<string[] | null>(null);
  const [lockedColIds, setLockedColIds] = useState<Set<string>>(new Set());
  const dragColRef = useRef<string | null>(null);


  // Only show enabled columns, sorted by column_order
  const baseEnabledCols = useMemo(
    () => columns.filter(c => c.is_enabled).sort((a, b) => a.column_order - b.column_order),
    [columns]
  );

  // Apply column order override + locked columns pinned to left
  const enabledCols = useMemo(() => {
    let orderedCols: ExportTemplateColumn[];
    if (columnOrderOverride) {
      const colMap = new Map(baseEnabledCols.map(c => [c.id, c]));
      orderedCols = columnOrderOverride
        .map(id => colMap.get(id))
        .filter(Boolean) as ExportTemplateColumn[];
      for (const col of baseEnabledCols) {
        if (!columnOrderOverride.includes(col.id)) {
          orderedCols.push(col);
        }
      }
    } else {
      orderedCols = [...baseEnabledCols];
    }

    if (lockedColIds.size > 0) {
      const locked = orderedCols.filter(c => lockedColIds.has(c.id));
      const unlocked = orderedCols.filter(c => !lockedColIds.has(c.id));
      return [...locked, ...unlocked];
    }

    return orderedCols;
  }, [baseEnabledCols, columnOrderOverride, lockedColIds]);

  // Only preview first 10 rows
  const previewRowsRaw = useMemo(() => rows, [rows]); // Show ALL rows — no limit


  // Generate preview data with margin results + invoice augmentation
  const previewData = useMemo(() => {
    if (collapsed && !poppedOut) return [];
    return previewRowsRaw.map(row => {
      const assignment = invoiceAssignments?.get(row.tc_id);
      const augmentedRow = augmentRowWithInvoiceData(row, assignment);
      const resolvedCalcColumns = new Map<string, string>();
      const outputRow: Record<string, { value: string; marginResult: MarginResult | null }> = {};
      for (const col of enabledCols) {
        const result = resolveColumnValueWithMargins(
          col, augmentedRow, aliasRules, marginRules, townClassifications, resolvedCalcColumns, rangeEndDate
        );
        outputRow[col.id] = result;
        if (col.source_type === 'calculated') {
          resolvedCalcColumns.set(col.id, result.value);
        }
      }
      return { outputRow, invoiceNumber: assignment?.assignedInvoiceNumber || null };
    });
  }, [previewRowsRaw, enabledCols, aliasRules, marginRules, invoiceAssignments, collapsed, poppedOut, townClassifications, rangeEndDate]);

  // ─── Multi-Column Sort ────────────────────────────────────────────
  const sortedPreviewData = useMemo(() => {
    if (sortSpecs.length === 0) return previewData;
    const sorted = [...previewData];
    sorted.sort((a, b) => {
      for (const spec of sortSpecs) {
        const aVal = a.outputRow[spec.colId]?.value || '';
        const bVal = b.outputRow[spec.colId]?.value || '';
        const aNum = parseFloat(aVal);
        const bNum = parseFloat(bVal);
        let cmp: number;
        if (!isNaN(aNum) && !isNaN(bNum)) {
          cmp = aNum - bNum;
        } else {
          cmp = aVal.localeCompare(bVal, undefined, { numeric: true, sensitivity: 'base' });
        }
        if (cmp !== 0) {
          return spec.direction === 'asc' ? cmp : -cmp;
        }
      }
      return 0;
    });
    return sorted;
  }, [previewData, sortSpecs]);

  // ─── Sort Handlers (header click for quick sort) ──────────────────

  const handleHeaderSortClick = useCallback((colId: string) => {
    const existingIdx = sortSpecs.findIndex(s => s.colId === colId);
    if (existingIdx >= 0 && sortSpecs.length === 1) {
      const existing = sortSpecs[existingIdx];
      if (existing.direction === 'asc') {
        onSortSpecsChange([{ colId, direction: 'desc' }]);
      } else {
        onSortSpecsChange([]);
      }
    } else {
      onSortSpecsChange([{ colId, direction: 'asc' }]);
    }
  }, [sortSpecs, onSortSpecsChange]);

  const handleAddToSort = useCallback((colId: string) => {
    if (sortSpecs.some(s => s.colId === colId)) return;
    onSortSpecsChange([...sortSpecs, { colId, direction: 'asc' }]);
  }, [sortSpecs, onSortSpecsChange]);

  const handleToggleSortDirection = useCallback((colId: string) => {
    onSortSpecsChange(
      sortSpecs.map(s =>
        s.colId === colId
          ? { ...s, direction: s.direction === 'asc' ? 'desc' : 'asc' }
          : s
      )
    );
  }, [sortSpecs, onSortSpecsChange]);

  const handleRemoveSort = useCallback((colId: string) => {
    onSortSpecsChange(sortSpecs.filter(s => s.colId !== colId));
  }, [sortSpecs, onSortSpecsChange]);


  // ─── Column Drag Handlers ─────────────────────────────────────────
  const handleColDragStart = useCallback((colId: string) => {
    dragColRef.current = colId;
  }, []);

  const handleColDrop = useCallback((targetColId: string) => {
    if (!dragColRef.current || dragColRef.current === targetColId) return;
    const currentOrder = columnOrderOverride || enabledCols.map(c => c.id);
    const next = [...currentOrder];
    const fromIdx = next.indexOf(dragColRef.current);
    const toIdx = next.indexOf(targetColId);
    if (fromIdx < 0 || toIdx < 0) return;
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, dragColRef.current);
    setColumnOrderOverride(next);
    dragColRef.current = null;
  }, [columnOrderOverride, enabledCols]);

  const toggleLockCol = useCallback((colId: string) => {
    setLockedColIds(prev => {
      const next = new Set(prev);
      if (next.has(colId)) next.delete(colId); else next.add(colId);
      return next;
    });
  }, []);

  // Count margin-adjusted cells
  const marginAdjustedCount = useMemo(() => {
    if (collapsed && !poppedOut) return 0;
    let count = 0;
    for (const { outputRow } of previewData) {
      for (const col of enabledCols) {
        if (outputRow[col.id]?.marginResult?.wasAdjusted) count++;
      }
    }
    return count;
  }, [previewData, enabledCols, collapsed, poppedOut]);

  if (enabledCols.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-8 text-center text-muted-foreground">
          <Layers className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No enabled template columns to preview.</p>
          <p className="text-xs mt-1 opacity-60">Enable columns in the template builder to see a preview.</p>
        </CardContent>
      </Card>
    );
  }

  if (previewRowsRaw.length === 0) {
    return (
      <Card className="bg-card border-border">
        <CardContent className="py-8 text-center text-muted-foreground">
          <FileSpreadsheet className="w-8 h-8 mx-auto mb-2 opacity-30" />
          <p className="text-sm">No data rows to preview.</p>
          <p className="text-xs mt-1 opacity-60">Adjust filters to load job data first.</p>
        </CardContent>
      </Card>
    );
  }

  const transformedCount = enabledCols.filter(c => c.transform_type && c.transform_type !== 'none').length;
  const activeMarginRules = marginRules.filter(r => r.is_active).length;
  const hasInvoiceData = invoiceAssignments && invoiceAssignments.size > 0;

  const getSortInfo = (colId: string) => {
    const idx = sortSpecs.findIndex(s => s.colId === colId);
    if (idx < 0) return null;
    return { priority: idx + 1, direction: sortSpecs[idx].direction };
  };

  // ─── Shared Table Renderer ────────────────────────────────────────
  const renderTable = (maxHeightClass: string) => (
    <div className={`overflow-auto ${maxHeightClass}`}>
      <table className="w-full text-xs">
        <thead className="sticky top-0 z-10 bg-card">
          <tr className="border-b border-border">
            <th className="px-2 py-1.5 text-[9px] font-medium text-muted-foreground/60 whitespace-nowrap text-center bg-card border-b border-border w-8">#</th>
            {hasInvoiceData && (
              <th className="px-2 py-1.5 text-[10px] font-medium text-indigo-400 whitespace-nowrap text-center bg-card border-b border-border w-16">
                <div className="flex items-center gap-0.5 justify-center">
                  <Hash className="w-3 h-3" />
                  <span>Inv #</span>
                </div>
              </th>
            )}
            {enabledCols.map(col => {
              const hasTransform = col.transform_type && col.transform_type !== 'none';
              const hasMarginRule = marginRules.some(r =>
                r.is_active && (
                  (col.source_type === 'source_field' && r.applies_to_field === col.source_field) ||
                  (col.source_type === 'calculated' && r.applies_to_field === col.export_column_name)
                )
              );
              const isInvoiceField = col.source_type === 'source_field' && col.source_field &&
                ['invoice_passenger_name', 'invoice_group_key', 'assigned_invoice_number'].includes(col.source_field);
              const isLocked = lockedColIds.has(col.id);
              const sortInfo = getSortInfo(col.id);
              const isAlreadySorted = sortInfo !== null;
              const hasSortsActive = sortSpecs.length > 0;

              return (
                <th
                  key={col.id}
                  className={`px-2 py-1.5 text-[10px] font-medium whitespace-nowrap text-left bg-card border-b border-border select-none cursor-pointer hover:bg-secondary/30 transition-colors group ${isLocked ? 'bg-amber-900/5 border-l border-l-amber-500/30' : ''}`}
                  style={{ minWidth: 80, maxWidth: 200 }}
                  draggable
                  onDragStart={() => handleColDragStart(col.id)}
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => handleColDrop(col.id)}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button')) return;
                    handleHeaderSortClick(col.id);
                  }}
                >
                  <div className="flex items-center gap-1">
                    <GripVertical className="w-2.5 h-2.5 text-muted-foreground/20 group-hover:text-muted-foreground/50 flex-shrink-0 cursor-grab" />

                    {isLocked && (
                      <Lock className="w-2.5 h-2.5 text-amber-400/70 flex-shrink-0" />
                    )}

                    <span className={`truncate ${sortInfo ? 'text-primary' : 'text-muted-foreground'}`}>
                      {col.export_column_name}
                    </span>

                    {sortInfo && (
                      <span className="flex items-center gap-0 flex-shrink-0">
                        {sortInfo.direction === 'asc' ? (
                          <ArrowUp className="w-3 h-3 text-primary" />
                        ) : (
                          <ArrowDown className="w-3 h-3 text-primary" />
                        )}
                        {sortSpecs.length > 1 && (
                          <span className="text-[8px] text-primary font-bold -ml-0.5">
                            *{sortInfo.priority}
                          </span>
                        )}
                      </span>
                    )}

                    {hasTransform && (
                      <span className="text-[8px] text-cyan-400/70 flex-shrink-0" title={`Transform: ${col.transform_type}`}>fx</span>
                    )}
                    {col.source_type === 'static_value' && (
                      <span className="text-[8px] text-green-400/70 flex-shrink-0" title="Static value">S</span>
                    )}
                    {col.source_type === 'blank' && (
                      <span className="text-[8px] text-gray-500/70 flex-shrink-0" title="Blank">B</span>
                    )}
                    {hasMarginRule && (
                      <span className="text-[8px] text-emerald-400/70 flex-shrink-0" title="Margin rule applied">
                        <TrendingUp className="w-2.5 h-2.5 inline" />
                      </span>
                    )}
                    {isInvoiceField && (
                      <span className="text-[8px] text-indigo-400/70 flex-shrink-0" title="Invoice field">
                        <Hash className="w-2.5 h-2.5 inline" />
                      </span>
                    )}

                    {hasSortsActive && !isAlreadySorted && (
                      <button
                        onClick={(e) => { e.stopPropagation(); handleAddToSort(col.id); }}
                        className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-primary/20"
                        title="Add this column to multi-sort"
                      >
                        <Plus className="w-2.5 h-2.5 text-primary/60" />
                      </button>
                    )}

                    <button
                      onClick={(e) => { e.stopPropagation(); toggleLockCol(col.id); }}
                      className={`flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity p-0.5 rounded hover:bg-secondary/50 ${isLocked ? '!opacity-100' : ''}`}
                      title={isLocked ? 'Unlock column' : 'Lock column in place'}
                    >
                      {isLocked ? (
                        <Unlock className="w-2.5 h-2.5 text-amber-400" />
                      ) : (
                        <Lock className="w-2.5 h-2.5 text-muted-foreground/30" />
                      )}
                    </button>
                  </div>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedPreviewData.map(({ outputRow, invoiceNumber }, rowIdx) => (
            <tr key={rowIdx} className="border-b border-border/30 hover:bg-secondary/20 transition-colors">
              <td className="px-2 py-1 text-[9px] text-muted-foreground/40 text-center">{rowIdx + 1}</td>
              {hasInvoiceData && (
                <td className="px-2 py-1 text-center">
                  {invoiceNumber ? (
                    <Badge className="bg-indigo-900/30 text-indigo-400 border-indigo-700/40 text-[9px] px-1.5 py-0 font-mono">
                      {invoiceNumber}
                    </Badge>
                  ) : (
                    <span className="text-muted-foreground/20">-</span>
                  )}
                </td>
              )}
              {enabledCols.map(col => {
                const cellData = outputRow[col.id] || { value: '', marginResult: null };
                const val = cellData.value;
                const mr = cellData.marginResult;
                const isEmpty = !val || val === '';
                const isCalcPlaceholder = val === '(calc)';
                const isAdjusted = mr?.wasAdjusted;
                const isLocked = lockedColIds.has(col.id);

                return (
                  <td
                    key={col.id}
                    className={`px-2 py-1 whitespace-nowrap ${isAdjusted ? 'bg-emerald-900/10' : ''} ${isLocked ? 'bg-amber-900/5 border-l border-l-amber-500/10' : ''}`}
                    style={{ maxWidth: 200 }}
                  >
                    {isCalcPlaceholder ? (
                      <span className="text-amber-400/60 text-[10px] italic">(calc)</span>
                    ) : isEmpty ? (
                      <span className="text-muted-foreground/20">-</span>
                    ) : isAdjusted && mr ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <span className="truncate block max-w-[190px] cursor-help">
                            <span className="text-emerald-400 font-medium">{val}</span>
                            <TrendingUp className="w-2.5 h-2.5 inline ml-0.5 text-emerald-500/60" />
                          </span>
                        </TooltipTrigger>
                        <TooltipContent side="top" className="bg-card border-border text-xs max-w-[260px]">
                          <div className="space-y-1">
                            <div className="flex items-center gap-1.5 text-[10px]">
                              <TrendingUp className="w-3 h-3 text-emerald-400" />
                              <span className="font-semibold text-emerald-400">Margin Applied</span>
                            </div>
                            <div className="text-[10px] text-muted-foreground space-y-0.5">
                              <div>Base: <span className="text-foreground">${mr.baseValue.toFixed(2)}</span></div>
                              {mr.adjustedPreRound !== mr.finalValue && (
                                <div>After margin: <span className="text-foreground">${mr.adjustedPreRound.toFixed(2)}</span></div>
                              )}
                              <div>Final: <span className="text-emerald-400 font-semibold">${mr.finalValue.toFixed(2)}</span></div>
                              {mr.appliedRule && (
                                <div className="pt-0.5 border-t border-border/40 mt-1">
                                  <span className="text-muted-foreground/70">Rule: </span>
                                  <span className="text-foreground">{mr.appliedRule.rule_name}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="truncate block max-w-[190px]" title={val}>{val}</span>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  // ─── Stats line ───────────────────────────────────────────────────
  const renderStats = () => (
    <div className="flex items-center gap-2 text-[10px] text-muted-foreground flex-wrap">
      <span>{enabledCols.length} columns</span>
      <span className="text-border">|</span>
      <span>{previewRowsRaw.length} of {rows.length} rows</span>
      {transformedCount > 0 && (
        <>
          <span className="text-border">|</span>
          <span className="text-cyan-400">{transformedCount} transformed</span>
        </>
      )}
      {activeMarginRules > 0 && (
        <>
          <span className="text-border">|</span>
          <span className="text-emerald-400">
            <TrendingUp className="w-3 h-3 inline mr-0.5" />
            {activeMarginRules} margin rule{activeMarginRules !== 1 ? 's' : ''}
          </span>
        </>
      )}
      {marginAdjustedCount > 0 && (
        <>
          <span className="text-border">|</span>
          <span className="text-emerald-400">{marginAdjustedCount} adjusted cells</span>
        </>
      )}
      {hasInvoiceData && invoiceSummary && (
        <>
          <span className="text-border">|</span>
          <span className="text-indigo-400">
            <Hash className="w-3 h-3 inline mr-0.5" />
            {invoiceSummary.uniqueGroups} invoices ({invoiceSummary.startingNumber}-{invoiceSummary.endingNumber})
          </span>
        </>
      )}
      {lockedColIds.size > 0 && (
        <>
          <span className="text-border">|</span>
          <span className="text-amber-400">
            <Lock className="w-3 h-3 inline mr-0.5" />
            {lockedColIds.size} locked
          </span>
        </>
      )}
      {sortSpecs.length > 0 && (
        <>
          <span className="text-border">|</span>
          <span className="text-primary">
            <ArrowUpDown className="w-3 h-3 inline mr-0.5" />
            {sortSpecs.length} sort{sortSpecs.length !== 1 ? 's' : ''}
          </span>
        </>
      )}
    </div>
  );

  // ─── Sort & Lock Summary Bar ──────────────────────────────────────
  const renderSortBar = () => {
    const hasSorts = sortSpecs.length > 0;
    const hasLocks = lockedColIds.size > 0;
    const hasReorder = !!columnOrderOverride;

    // Only show bar if there's something to display
    if (!hasSorts && !hasLocks && !hasReorder) return null;

    return (
      <div className="px-4 py-1.5 border-b border-border/30 flex items-center gap-2 flex-wrap bg-secondary/10">
        {/* ── Sort chips ─────────────────────────────────────────── */}
        {hasSorts && (
          <>
            <div className="flex items-center gap-1.5">
              <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
              <span className="text-[10px] text-muted-foreground/60 font-medium">Sort:</span>
            </div>
            {sortSpecs.map((spec, idx) => {
              const col = enabledCols.find(c => c.id === spec.colId);
              return (
                <div
                  key={spec.colId}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-primary/10 border border-primary/25 text-[10px] text-primary"
                >
                  {sortSpecs.length > 1 && <span className="font-bold text-primary/70">#{idx + 1}</span>}
                  <span className="font-medium">{col?.export_column_name || '?'}</span>
                  <button
                    onClick={() => handleToggleSortDirection(spec.colId)}
                    className="hover:bg-primary/20 rounded p-0.5 transition-colors"
                    title={`${spec.direction === 'asc' ? 'Ascending' : 'Descending'} — click to toggle`}
                  >
                    {spec.direction === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}
                  </button>
                  <button
                    onClick={() => handleRemoveSort(spec.colId)}
                    className="hover:bg-red-500/20 hover:text-red-400 rounded-full p-0.5 transition-colors"
                    title="Remove from sort"
                  >
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              );
            })}
            <button
              onClick={() => onSortSpecsChange([])}
              className="text-[10px] text-muted-foreground/50 hover:text-red-400 transition-colors px-1.5 py-0.5 rounded hover:bg-red-500/10"
            >
              Clear
            </button>
          </>
        )}

        {/* ── Separator ────────────────────────────────────────────── */}
        {hasSorts && (hasLocks || hasReorder) && (
          <span className="text-border mx-1">|</span>
        )}

        {/* Locked columns chips */}
        {hasLocks && (
          <>
            <span className="text-[10px] text-muted-foreground/60 font-medium">Locked:</span>
            {Array.from(lockedColIds).map(colId => {
              const col = enabledCols.find(c => c.id === colId);
              return (
                <div
                  key={colId}
                  className="flex items-center gap-1 px-2 py-0.5 rounded-md bg-amber-900/20 border border-amber-700/20 text-[10px] text-amber-400"
                >
                  <Lock className="w-2.5 h-2.5" />
                  <span className="font-medium">{col?.export_column_name || '?'}</span>
                  <button onClick={() => toggleLockCol(colId)} className="hover:bg-amber-900/30 rounded-full p-0.5">
                    <X className="w-2.5 h-2.5" />
                  </button>
                </div>
              );
            })}
          </>
        )}
        {hasReorder && (
          <button
            onClick={() => setColumnOrderOverride(null)}
            className="text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors px-1.5 py-0.5 rounded hover:bg-secondary/50"
          >
            Reset column order
          </button>
        )}
        <div className="flex-1" />
        <span className="text-[9px] text-muted-foreground/30 italic hidden lg:inline">
          Click header to quick-sort · Use Sort button in toolbar for multi-column sort
        </span>
      </div>
    );
  };


  return (
    <TooltipProvider delayDuration={200}>
      <Card className="bg-card border-border">
        <CardHeader
          className="pb-2 pt-3 px-4 cursor-pointer select-none hover:bg-secondary/20 transition-colors"
          onClick={(e) => {
            if ((e.target as HTMLElement).closest('button')) return;
            setCollapsed(!collapsed);
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {collapsed ? (
                <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform" />
              )}
              <Eye className="w-4 h-4 text-primary" />
              <CardTitle className="text-sm font-semibold">Template Output Preview</CardTitle>
              {templateName && (
                <Badge className="bg-secondary text-muted-foreground text-[10px] px-1.5 py-0">{templateName}</Badge>
              )}
              {collapsed && (
                <span className="text-[10px] text-muted-foreground/60 ml-1">
                  {enabledCols.length} columns · {previewRowsRaw.length} of {rows.length} rows
                  {transformedCount > 0 && ` · ${transformedCount} transformed`}
                  {activeMarginRules > 0 && ` · ${activeMarginRules} margin rules`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {!collapsed && renderStats()}
              {!collapsed && (
                <Button
                  size="sm" variant="ghost"
                  onClick={(e) => { e.stopPropagation(); setPoppedOut(true); }}
                  className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground ml-2"
                  title="Pop out to full-screen view"
                >
                  <Maximize2 className="w-3 h-3 mr-1" />
                  Pop Out
                </Button>
              )}
            </div>
          </div>
        </CardHeader>

        {!collapsed && (
          <CardContent className="p-0">
            {renderSortBar()}
            {renderTable('max-h-[350px]')}

            <div className="px-4 py-2 border-t border-border/40 flex items-center gap-2">
              <AlertCircle className="w-3 h-3 text-muted-foreground/40" />
              <span className="text-[10px] text-muted-foreground/60">
                Preview — showing first {previewRowsRaw.length} rows with current transforms
                {activeMarginRules > 0 ? ', margin rules' : ''}
                {hasInvoiceData ? ', and invoice numbers' : ''} applied. Use the Export button to download.
              </span>
            </div>
          </CardContent>
        )}
      </Card>

      {/* Pop-Out Dialog */}
      <Dialog open={poppedOut} onOpenChange={setPoppedOut}>
        <DialogContent className="max-w-[95vw] w-[95vw] max-h-[92vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 py-3 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Eye className="w-4 h-4 text-primary" />
                <DialogTitle className="text-sm font-semibold">Template Output Preview — Expanded View</DialogTitle>
                {templateName && (
                  <Badge className="bg-secondary text-muted-foreground text-[10px] px-1.5 py-0">{templateName}</Badge>
                )}
              </div>
              <div className="flex items-center gap-3">
                {renderStats()}
                <Button
                  size="sm" variant="ghost"
                  onClick={() => setPoppedOut(false)}
                  className="h-7 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Minimize2 className="w-3.5 h-3.5 mr-1" />
                  Collapse
                </Button>
              </div>
            </div>
            <DialogDescription className="sr-only">
              Full-screen view of the template output preview table for easier reading and scrolling.
            </DialogDescription>
          </DialogHeader>

          {renderSortBar()}

          <div className="flex-1 overflow-hidden">
            {renderTable('h-full')}
          </div>

          <div className="px-5 py-2 border-t border-border/40 flex items-center gap-2 flex-shrink-0">
            <AlertCircle className="w-3 h-3 text-muted-foreground/40" />
            <span className="text-[10px] text-muted-foreground/60">
              Preview — showing first {previewRowsRaw.length} rows with current transforms
              {activeMarginRules > 0 ? ', margin rules' : ''}
              {hasInvoiceData ? ', and invoice numbers' : ''} applied. Use the Export button to download.
            </span>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default OutputPreview;
